import {
  DEFAULT_THRESHOLDS,
  METRIC_FEATURES,
  MINIMUM_WINDOWS,
  SEVERITY_RANK,
  TARGET_WINDOWS,
} from "./constants";
import { buildCombinedWindows } from "./combine";
import { evaluateRules, scoreModel } from "./scoring";
import type {
  Alert,
  AlertSeverity,
  AlertStatus,
  AnalysisData,
  CombinedWindow,
  DriftFeature,
  ImportedReport,
  Incident,
  MetricRecord,
  ModelArtifact,
  ParserDiagnostics,
  QualityCheck,
  QualityReport,
  ReadinessReport,
  RuleThresholds,
  SecurityEvent,
} from "../types";

const EMPTY_DIAGNOSTICS: ParserDiagnostics = {
  lines: 0,
  validObjects: 0,
  malformedRows: 0,
  invalidTimestamps: 0,
  missingFeatureValues: 0,
  secretExposures: 0,
  duplicateRows: 0,
};

function severityForRatio(ratio: number): AlertSeverity {
  if (ratio >= 5) return "critical";
  if (ratio >= 2.5) return "high";
  if (ratio >= 1) return "medium";
  return "informational";
}

function highestSeverity(values: AlertSeverity[]): AlertSeverity {
  return values.reduce(
    (best, value) => (SEVERITY_RANK[value] > SEVERITY_RANK[best] ? value : best),
    "informational",
  );
}

function alertTitle(window: CombinedWindow, rules: ReturnType<typeof evaluateRules>): string {
  if (rules.length) {
    const id = rules[0].rule_id;
    if (id === "authentication_failure_burst") return "Authentication burst";
    if (id === "process_start_burst") return "Process launch burst";
    if (id === "package_change_burst") return "Package change burst";
    if (id === "privilege_elevation_burst") return "Privilege activity burst";
  }
  if (window.scenario && window.scenario !== "normal") {
    return window.scenario.replaceAll("_", " ").replace(/\b\w/g, (value) => value.toUpperCase());
  }
  return "Behavior anomaly";
}

function buildAlerts(
  windows: CombinedWindow[],
  model: ModelArtifact | null,
  thresholds: RuleThresholds,
  statuses: Record<string, AlertStatus>,
): Alert[] {
  const alerts: Alert[] = [];
  for (const window of windows) {
    const rules = evaluateRules(window, thresholds);
    const modelScore = model ? scoreModel(model, window) : null;
    const scenarioRatio = window.scenario && window.scenario !== "normal" ? 3.2 : 0;
    const finalRatio = Math.max(
      modelScore?.ratio ?? 0,
      scenarioRatio,
      ...rules.map((rule) => rule.ratio_floor),
    );
    if (finalRatio < 1) continue;
    const severity = highestSeverity([
      severityForRatio(finalRatio),
      ...rules.map((rule) => rule.severity),
    ]);
    const id = `${window.host}:${window.timestamp}`;
    const description = rules[0]?.description ??
      (modelScore
        ? `Model reconstruction error is ${modelScore.ratio.toFixed(2)} times the threshold`
        : "Synthetic scenario differs from the normal baseline");
    alerts.push({
      id,
      timestamp: window.timestamp,
      host: window.host,
      severity,
      status: statuses[id] ?? "open",
      scoreRatio: finalRatio,
      modelRatio: modelScore?.ratio ?? scenarioRatio,
      title: alertTitle(window, rules),
      summary: description,
      rules,
      topFeatures: modelScore?.topFeatures ?? scenarioTopFeatures(window),
      scenario: window.scenario,
    });
  }
  return alerts.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}

function scenarioTopFeatures(window: CombinedWindow) {
  const names = METRIC_FEATURES.map((feature) => ({
    feature,
    contribution: Math.abs(Number(window[feature] ?? 0)),
  }));
  return names.sort((a, b) => b.contribution - a.contribution).slice(0, 5);
}

export function groupIncidents(alerts: Alert[], maxGapMinutes = 15): Incident[] {
  const byHost = new Map<string, Alert[]>();
  for (const alert of [...alerts].sort((a, b) => a.timestamp.localeCompare(b.timestamp))) {
    byHost.set(alert.host, [...(byHost.get(alert.host) ?? []), alert]);
  }
  const incidents: Incident[] = [];
  for (const [host, hostAlerts] of byHost) {
    let current: Alert[] = [];
    for (const alert of hostAlerts) {
      const previous = current.at(-1);
      const gap = previous
        ? (Date.parse(alert.timestamp) - Date.parse(previous.timestamp)) / 60_000
        : 0;
      if (current.length && gap > maxGapMinutes) {
        incidents.push(makeIncident(host, current));
        current = [];
      }
      current.push(alert);
    }
    if (current.length) incidents.push(makeIncident(host, current));
  }
  return incidents.sort((a, b) => b.lastTimestamp.localeCompare(a.lastTimestamp));
}

function makeIncident(host: string, alerts: Alert[]): Incident {
  const first = alerts[0];
  const last = alerts.at(-1) ?? first;
  const severity = highestSeverity(alerts.map((alert) => alert.severity));
  const status: AlertStatus = alerts.every((alert) => alert.status === "resolved")
    ? "resolved"
    : alerts.some((alert) => alert.status === "acknowledged")
      ? "acknowledged"
      : "open";
  return {
    id: `${host}:${first.timestamp}:${alerts.length}`,
    host,
    firstTimestamp: first.timestamp,
    lastTimestamp: last.timestamp,
    severity,
    status,
    alertIds: alerts.map((alert) => alert.id),
    title: alerts.length === 1 ? first.title : `${alerts.length} related security signals`,
  };
}

function validateOcsf(event: SecurityEvent): boolean {
  if (!event.ocsf || typeof event.ocsf !== "object") return true;
  const value = event.ocsf;
  const classUid = Number(value.class_uid);
  const activityId = Number(value.activity_id);
  return (
    Number.isInteger(classUid) &&
    Number.isInteger(activityId) &&
    Number(value.type_uid) === classUid * 100 + activityId &&
    Number.isFinite(Number(value.time)) &&
    typeof value.metadata === "object"
  );
}

function qualityReport(
  metrics: MetricRecord[],
  events: SecurityEvent[],
  diagnostics: ParserDiagnostics,
): QualityReport {
  const metricKeys = new Set<string>();
  const eventKeys = new Set<string>();
  let duplicateMetrics = diagnostics.duplicateRows;
  let duplicateEvents = 0;
  let missingFeatureValues = diagnostics.missingFeatureValues;
  let invalidTimestamps = diagnostics.invalidTimestamps;
  for (const row of metrics) {
    if (!Number.isFinite(Date.parse(row.timestamp))) invalidTimestamps += 1;
    const identity = `${row.host}\u001f${row.timestamp}`;
    if (metricKeys.has(identity)) duplicateMetrics += 1;
    metricKeys.add(identity);
    missingFeatureValues += METRIC_FEATURES.filter(
      (name) => row[name] === undefined || row[name] === null || !Number.isFinite(Number(row[name])),
    ).length;
  }
  let ocsfNative = 0;
  let ocsfInvalid = 0;
  for (const event of events) {
    if (!Number.isFinite(Date.parse(event.timestamp))) invalidTimestamps += 1;
    const identity = event.event_id || `${event.host}\u001f${event.timestamp}\u001f${event.event_type}`;
    if (eventKeys.has(identity)) duplicateEvents += 1;
    eventKeys.add(identity);
    if (event.ocsf) {
      ocsfNative += 1;
      if (!validateOcsf(event)) ocsfInvalid += 1;
    }
  }
  const checks: QualityCheck[] = [
    check("json", "JSONL structure", diagnostics.malformedRows, "complete malformed rows"),
    check("timestamps", "UTC timestamps", invalidTimestamps, "invalid timestamps"),
    check("features", "Required metric features", missingFeatureValues, "missing or non-numeric values"),
    warningCheck("duplicates", "Duplicate identities", duplicateMetrics + duplicateEvents, "duplicates"),
    check("privacy", "Persisted-secret scan", diagnostics.secretExposures, "possible secret exposures"),
    check("ocsf", "OCSF core invariants", ocsfInvalid, "invalid native envelopes"),
  ];
  return {
    metricsValid: metrics.length,
    eventsValid: events.length,
    malformedRows: diagnostics.malformedRows,
    invalidTimestamps,
    missingFeatureValues,
    duplicateMetrics,
    duplicateEvents,
    secretExposures: diagnostics.secretExposures,
    ocsfNative,
    ocsfInvalid,
    checks,
  };
}

function check(id: string, name: string, count: number, noun: string): QualityCheck {
  return {
    id,
    name,
    status: count ? "fail" : "pass",
    detail: count ? `${count.toLocaleString()} ${noun}` : "No issues found",
    count,
  };
}

function warningCheck(id: string, name: string, count: number, noun: string): QualityCheck {
  return {
    id,
    name,
    status: count ? "warn" : "pass",
    detail: count ? `${count.toLocaleString()} ${noun}` : "No issues found",
    count,
  };
}

function readinessReport(
  windows: CombinedWindow[],
  quality: QualityReport,
  thresholds: RuleThresholds,
): ReadinessReport {
  const byHost = new Map<string, Set<number>>();
  let excludedWindows = 0;
  for (const window of windows) {
    const minute = Math.floor(Date.parse(window.timestamp) / 60_000);
    const values = byHost.get(window.host) ?? new Set<number>();
    values.add(minute);
    byHost.set(window.host, values);
    if (evaluateRules(window, thresholds).length || Number(window.event_collector_error_count ?? 0) > 0) {
      excludedWindows += 1;
    }
  }
  let expectedWindows = 0;
  let observedWindows = 0;
  let gapCount = 0;
  let largestGapMinutes = 0;
  for (const values of byHost.values()) {
    const ordered = [...values].sort((a, b) => a - b);
    observedWindows += ordered.length;
    if (ordered.length) expectedWindows += ordered.at(-1)! - ordered[0] + 1;
    for (let index = 1; index < ordered.length; index += 1) {
      const gap = ordered[index] - ordered[index - 1];
      if (gap > 1) {
        gapCount += 1;
        largestGapMinutes = Math.max(largestGapMinutes, gap - 1);
      }
    }
  }
  const missingWindows = Math.max(0, expectedWindows - observedWindows);
  const coverageRatio = expectedWindows ? observedWindows / expectedWindows : 0;
  const cleanWindows = Math.max(0, observedWindows - excludedWindows);
  const warnings: string[] = [];
  if (coverageRatio < 0.95) warnings.push("Coverage is below the 95% release threshold");
  if (largestGapMinutes > 5) warnings.push("A collection gap is longer than five minutes");
  if (excludedWindows) warnings.push(`${excludedWindows} suspicious or unhealthy windows are excluded`);
  const hasErrors = quality.checks.some((item) => item.status === "fail");
  let state: ReadinessReport["state"];
  if (hasErrors) state = "invalid";
  else if (cleanWindows < MINIMUM_WINDOWS) state = "insufficient";
  else if (cleanWindows < TARGET_WINDOWS) state = "preliminary";
  else if (warnings.length) state = "ready_with_warnings";
  else state = "ready";
  return {
    state,
    observedWindows,
    expectedWindows,
    missingWindows,
    coverageRatio,
    cleanWindows,
    excludedWindows,
    targetWindows: TARGET_WINDOWS,
    gapCount,
    largestGapMinutes,
    warnings,
  };
}

function median(values: number[]): number {
  if (!values.length) return 0;
  const ordered = [...values].sort((a, b) => a - b);
  const middle = Math.floor(ordered.length / 2);
  return ordered.length % 2 ? ordered[middle] : (ordered[middle - 1] + ordered[middle]) / 2;
}

function driftReport(reference: MetricRecord[], current: MetricRecord[]): DriftFeature[] {
  if (!reference.length || !current.length) return [];
  return METRIC_FEATURES.map((feature) => {
    const referenceValues = reference.map((row) => Number(row[feature] ?? 0));
    const currentValues = current.map((row) => Number(row[feature] ?? 0));
    const referenceMedian = median(referenceValues);
    const currentMedian = median(currentValues);
    const mad = median(referenceValues.map((value) => Math.abs(value - referenceMedian)));
    const scale = Math.max(mad * 1.4826, Math.abs(referenceMedian) * 0.01, 1e-6);
    const robustShift = Math.abs(currentMedian - referenceMedian) / scale;
    return { feature, referenceMedian, currentMedian, robustShift, drifted: robustShift >= 4 };
  }).sort((a, b) => b.robustShift - a.robustShift);
}

export interface AnalyzeInput {
  sourceName: string;
  metrics: MetricRecord[];
  events: SecurityEvent[];
  referenceMetrics?: MetricRecord[];
  model?: ModelArtifact | null;
  reports?: ImportedReport[];
  thresholds?: RuleThresholds;
  statuses?: Record<string, AlertStatus>;
  diagnostics?: Partial<ParserDiagnostics>;
}

export function analyze(input: AnalyzeInput): AnalysisData {
  const thresholds = input.thresholds ?? DEFAULT_THRESHOLDS;
  const diagnostics = { ...EMPTY_DIAGNOSTICS, ...(input.diagnostics ?? {}) };
  const windows = buildCombinedWindows(input.metrics, input.events);
  const quality = qualityReport(input.metrics, input.events, diagnostics);
  const alerts = buildAlerts(windows, input.model ?? null, thresholds, input.statuses ?? {});
  const reference = input.referenceMetrics ?? [];
  const currentForDrift = reference.length ? input.metrics : input.metrics.slice(Math.floor(input.metrics.length / 2));
  const referenceForDrift = reference.length ? reference : input.metrics.slice(0, Math.floor(input.metrics.length / 2));
  return {
    sourceName: input.sourceName,
    importedAt: new Date().toISOString(),
    metrics: input.metrics,
    events: input.events,
    referenceMetrics: reference,
    windows,
    alerts,
    incidents: groupIncidents(alerts),
    quality,
    readiness: readinessReport(windows, quality, thresholds),
    drift: driftReport(referenceForDrift, currentForDrift),
    model: input.model ?? null,
    reports: input.reports ?? [],
  };
}
